import { useQuery } from '@tanstack/react-query';
import http from 'helpers/requests';

/**
 * Hook to fetch the organization's plan summary.
 *
 * GET /plan/summary
 *
 * Returns: {
 *   status: 'active' | 'expired' | 'none',
 *   plan: { slug, name, entity } | null,
 *   price?: number | null,      // null when no price is recorded
 *   currency?: 'GBP' | 'EUR',   // derived from the org, not stored per plan
 *   startsAt?: string,          // ISO
 *   expiresAt?: string | null,  // ISO; null when the plan is permanent
 *   isPermanent?: boolean,
 *   daysRemaining?: number | null,
 *   entitlements?: string[]
 * }
 *
 * `price: null` means "not recorded" and must not be rendered as free or as 0 —
 * a recorded price of 0 is a distinct, legitimate value.
 *
 * `expiresAt: null` together with `isPermanent: true` means the plan does not
 * expire — it never means "unknown". `status: 'none'` means the org has no plan
 * context at all (a consultant org, or an entity with no assignment yet).
 *
 * The backend is the plan authority. `organization.publicMetadata.plan` is a
 * synchronous fast path for routing guards only and carries no dates, so it
 * must not be used to render this surface.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Whether to run the query (default: true)
 * @param {number} options.staleTime - ms before data is stale (default: 5 min)
 * @param {number} options.gcTime - ms to keep data cached (default: 10 min)
 */
const usePlanSummary = (options = {}) => {
  const {
    enabled = true,
    staleTime = 300000, // 5 minutes — plan changes are rare.
    gcTime = 600000, // 10 minutes
  } = options;

  return useQuery({
    queryKey: ['planSummary'],
    queryFn: async () => {
      const response = await http.get('/plan/summary');
      return response.data;
    },
    enabled,
    staleTime,
    gcTime,
    retry: 2,
  });
};

export default usePlanSummary;
